import React, { FC, useState } from "react";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import styled from "styled-components";
import { palette } from "../styled/colors";
import { useLoadScript } from "@react-google-maps/api";

const libraries: any = ["places"]; // Required library

interface IGooglePlacesInput {
  defaultValue?: string;
  onSelect: (address: string) => void;
  // setSelectedPlace: React.Dispatch<React.SetStateAction<string>>;
  style?: any;
  inputStyle?: any;
  placeholder?: string;
  saveLatLng?: ({ lat, lng }: { lat: number; lng: number }) => void;
}

const GooglePlacesInput: FC<IGooglePlacesInput> = ({
  defaultValue,
  onSelect,
  // setSelectedPlace,
  style,
  inputStyle,
  placeholder,
  saveLatLng,
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "AIzaSyCyeed677ICVk7ZvQARsvHpE0P5Mjgx52Q",
    libraries,
  });

  const [address, setAddress] = useState(defaultValue ?? "");

  // This function formats the address to the required format (number, street, city, state abbreviated, zip code)
  const formatAddress = (results: any) => {
    if (!results || !results.length) return "";
    
    const addressComponents = results[0].address_components;
    let streetNumber = "";
    let street = "";
    let city = "";
    let stateAbbreviation = "";
    let zipCode = "";

    // Extract the relevant address components
    addressComponents.forEach((component: any) => {
      const types = component.types;
      
      if (types.includes("street_number")) {
        streetNumber = component.short_name;
      } else if (types.includes("route")) {
        street = component.long_name;
      } else if (types.includes("locality") || types.includes("sublocality")) {
        city = component.long_name;
      } else if (types.includes("administrative_area_level_1")) {
        stateAbbreviation = component.short_name; // Get abbreviated state
      } else if (types.includes("postal_code")) {
        zipCode = component.short_name;
      }
    });

    // Build the formatted address string
    let formattedAddress = "";

    if (streetNumber && street) {
      formattedAddress += `${streetNumber} ${street}`;
    }

    if (city) {
      formattedAddress += formattedAddress ? `, ${city}` : city;
    }

    if (stateAbbreviation) {
      formattedAddress += formattedAddress ? `, ${stateAbbreviation}` : stateAbbreviation;
    }

    if (zipCode) {
      formattedAddress += formattedAddress ? ` ${zipCode}` : zipCode;
    }

    return formattedAddress;
  };

  const handleSelect = (addr: string) => {
    if (addr !== "") {
      geocodeByAddress(addr)
        .then((results) => {
          // Format address to the required format
          const formattedAddress = formatAddress(results);
          setAddress(formattedAddress);
          onSelect(formattedAddress);
          
          // If latitude and longitude are needed
          if (saveLatLng) {
            return getLatLng(results[0]).then((latLng) => {
              saveLatLng(latLng);
            });
          }
        })
        .catch((error) => console.error("Error", error));
    }
  };

  if (loadError) {
    return <div>Error loading Google Maps API</div>;
  }

  if (!isLoaded) {
    return <div>Loading...</div>;
  }

  return (
    <div style={style}>
      <PlacesAutocomplete
        value={address}
        onChange={(loc) => {
          setAddress(loc);
          if (loc === "") {
            onSelect("");
          }
        }}
        onSelect={handleSelect}
        googleCallbackName="initMap"
        // Add search options to focus on addresses with postal codes
        searchOptions={{
          types: ['address'],
        }}
      >
        {({ getInputProps, suggestions, getSuggestionItemProps }: any) => (
          <div style={{ flex: "1 1 0%" }}>
            <>
              <LocationInputWrapper>
                <Location src="/images/icons/location_icon.svg" />

                <GoogleAutoLocation
                  borderRadius={"0.2rem"}
                  margin={"0"}
                  padding={"0.8rem 0rem 0.8rem 3rem"}
                  value={address}
                  {...getInputProps()}
                  placeholder={
                    placeholder || "Enter Zip Code, City, Neighborhood"
                  }
                  style={inputStyle}
                />
              </LocationInputWrapper>
              <ListItems
                open={suggestions?.length > 0}
                style={{ zIndex: "100" }}
              >
                {suggestions?.map((suggestion: any, i: any) => {
                  const style = {
                    backgroundColor: suggestion.active ? "#32CD32" : "white",
                  };
                  return (
                    <LocationList
                      key={i + "mapkey"}
                      {...getSuggestionItemProps(suggestion, {
                        style,
                      })}
                    >
                      {suggestion?.description}
                    </LocationList>
                  );
                })}
              </ListItems>
            </>
          </div>
        )}
      </PlacesAutocomplete>
    </div>
  );
};

const LocationInputWrapper = styled.div`
  position: relative;
`;

const Location = styled.img`
  position: absolute;
  top: 25%;
  left: 5px;
  margin: auto;
  width: 20px;
  height: 18px;
`;

const GoogleAutoLocation = styled.input<any>`
  font-family: "Lato-Regular", sans-serif;
  font-style: italic;
  line-height: normal;
  outline: none;
  font-weight: 400;
  text-align: left;
  font-size: 12px;
  border-radius: 0.5rem;
  padding: 0.45rem 0.5rem 0.45rem 1.5rem;
  border: 1px solid ${palette.stroke};
  background: #f0f2f5;
  width: 100%;
  color: #4b4b4b;
  &:focus {
    border: 1px solid
      ${({ error, disabled }) =>
        disabled ? "none" : error ? palette.danger : palette.Btn_dark_green};
  }
  &::placeholder {
    color: ${palette.gray_100};
  }

  &:-ms-input-placeholder {
    /* Internet Explorer 10-11 */
    color: ${({ disabled, isDarkTheme }) =>
      disabled || isDarkTheme ? `${palette.silver}` : `${palette.gray_100}`};
  }

  &::-ms-input-placeholder {
    /* Microsoft Edge */
    // color: ${palette.gray_100};
  }
`;

const ListItems = styled.div<any>`
	position: absolute;
	background: ${palette.white};
	z - index: 1;
	width: calc(100 % - 1.875rem);
	border: ${({ open }) => (open ? `1px solid ${palette.stroke}` : null)};

	border - radius: 1rem;

	max - height: 15rem;
	overflow - y: auto;
`;

const LocationList = styled.div`
	background - color: ${palette.white};
	cursor: pointer;
	line - height: 2rem;
	padding: 0.5rem 0.8rem;
	border - bottom: 1px solid ${palette.stroke};
	font - family: 'Lato-Regular', sans - serif;
	text - transform: normal;
	color: ${palette.heading_color};
		&:hover {
		background: ${palette.Btn_dark_green};
		color: ${palette.white};
	}

		&: first - child {
		border - top - left - radius: 0.375rem;
		border - top - right - radius: 0.375rem;
	}
		&: last - child {
		border - bottom - left - radius: 0.375rem;
		border - bottom - right - radius: 0.375rem;
	}
		&: last - child {
		border - bottom: 0;
	}
`;

export default GooglePlacesInput;
